import { Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { MaterialAutocomplete } from '../../../common/formik-material-ui';
import { teamAgentIds } from '../../../recoil/atoms/teamAgentIds';
import {
  methodNameEnum,
  refetchToggle,
} from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import { ModalWithStyles } from './ModalWithStyles';

const useStyles = makeStyles((theme) => ({
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
}));
// modal for adding agent to a TEAM
export function AddAgentModal({
  id,
  show,
  teamName,
  userAtom,
  setUserAtom,
  handleClose,
  teams,
  enqueueSnackbarWithStyles,
  viewTeam,
}) {
  const classes = useStyles();
  const arrayOfIdsToFilter = teams?.map((team) => team.userid);
  const setAgentIdsToAdd = useSetRecoilState(teamAgentIds);
  const setRefetchToggleForBusinessTeams = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_TEAMS)
  );
  const setRefetchToggleForTeamSettings = useSetRecoilState(
    refetchToggle(endpointEnum.GET_TEAM_SETTINGS)
  );
  const data = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };
  const businessUsers = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const users = useMemo(
    () =>
      businessUsers.state === 'hasValue'
        ? businessUsers.contents?.response?.data?.result.filter(
            (user) => user.isactive === 1
          )
        : [],
    [businessUsers, userAtom]
  );
  const [userIds, setUserIds] = useState([]);
  const { id: idFromUrl } = useParams();
  const onChangeForAutocomplete = (event, newValue) => {
    // keep autocomplete in sync
    setUserIds(
      newValue // update value for type
    );
  };
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));
  return (
    <Formik
      onSubmit={async (values, actions) => {
        // refactor to makephpreq
        const idArr = userIds.map((user) => user.userid);
        // if we are accessing this component from the view team settings component then queue the agent ids to add to the team in the atom
        if (viewTeam) {
          setAgentIdsToAdd(idArr);
        } else {
          const data = {
            call: methodNameEnum.ADD_AGENT_TEAM,
            apikey: userAtom.contents.apikey,
            businessid: userAtom.contents.businessid,
            teamid: values.teamid,
            ids: JSON.stringify(idArr),
          };
          const response = await mutation.mutateAsync({
            endpoint: endpointEnum.ADD_AGENT_TEAM,
            payload: data,
          });
          if (response?.data?.success) {
            enqueueSnackbarWithStyles({
              message: 'Successfully Added Team Member(s)',
              alertProps: { severity: 'success' },
            });
          } else {
            enqueueSnackbarWithStyles({
              message: 'Error Adding Team Member(s)',
              alertProps: { severity: 'warning' },
            });
          }
          setRefetchToggleForBusinessTeams({});
          setRefetchToggleForTeamSettings({});
        }
        handleClose();
        actions.resetForm({});
      }}
      enableReinitialize={true}
      initialValues={{
        teamid: id ?? idFromUrl,
      }}
    >
      {(formik) => (
        <ModalWithStyles
          onSubmit={formik.handleSubmit}
          title={`Add Agents to ${teamName}`}
          show={show}
          onHide={handleClose}
          isSubmitting={formik.isSubmitting}
          isLoading={formik.isSubmitting}
          size={'sm'}
          footerComponent={
            <div style={{ justifyContent: 'flex-end' }}>
              <Button
                className={classes.button}
                disableElevation
                variant="contained"
                disabled={formik.isSubmitting}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                className={classes.button}
                style={{ marginRight: '10px' }}
                disableElevation
                variant="contained"
                color="primary"
                disabled={formik.isSubmitting || userIds.length === 0}
                onClick={formik.handleSubmit}
              >
                Save
              </Button>
            </div>
          }
        >
          <MaterialAutocomplete
            type="text"
            fullWidth
            label="Team Member"
            size="small"
            variant="outlined"
            multiple={true}
            options={users?.filter(
              (user) => !arrayOfIdsToFilter?.includes(user.userid)
            )}
            onChange={onChangeForAutocomplete}
            getOptionLabel={(user) => user?.fname + ' ' + user.lname ?? ''}
            isOptionEqualToValue={(user, value) =>
              user?.userid === value?.userid
            }
          />
          <br></br>
        </ModalWithStyles>
      )}
    </Formik>
  );
}
